type RtCondition = {
  client_id: string
  permission_type: number
  request_able: boolean
}

type UnknownRecord = Record<string, any>

type AutoRtArgs = [RtCondition[], UnknownRecord, string, UnknownRecord, UnknownRecord]

const createEmptyAutoRTInfo = (): UnknownRecord => ({
  logoImage: '',
  clientId: '',
  dialogName: '',
  clientName: '',
  termurl: '',
  privacyurl: '',
  occupationurl: '',
  handlingurl: '',
  clientServiceName: '',
  privacyLabel: '',
  showTrailingBr: false,
  permissionType: 0,
  oldPermissionType: 0
})

type DownloadItemVm = {
  $firebase: { user: { uid: string } }
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $axios: { post: (url: string, data: FormData, config: { timeout: number }) => Promise<any> }
  $varuna: { endPoints: { displayDialog: string } }
  $globalVar: UnknownRecord
}

export function useDownloadItemAutoRt (vm: DownloadItemVm) {
  const hasRequestable = (rtconditions: RtCondition[], clientId: string, permissionType: number) => {
    const matched = rtconditions.find(item =>
      item.client_id === clientId && item.permission_type === permissionType
    )
    return !!(matched && matched.request_able)
  }

  const hasOldAndNewRequestable = (rtconditions: RtCondition[], clientId: string, newPermissionType: number, oldPermissionType: number) => {
    return hasRequestable(rtconditions, clientId, oldPermissionType) &&
      hasRequestable(rtconditions, clientId, newPermissionType)
  }

  const displayDialog = (formatid: string, clientId: string, permissionType: number) => {
    const formData = new FormData()
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid)
    formData.append('dialog_name', 'auto_rt')
    formData.append('client_id', clientId)
    formData.append('permission_type', String(permissionType))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('site_flag', 'r')
    formData.append('app_flag', vm.$site.appFlag)
    vm.$axios.post(vm.$varuna.endPoints.displayDialog, formData, { timeout: 60000 }).catch((error: unknown) => {
      console.error('[useDownloadItemAutoRt] displayDialog failed', {
        formatid,
        clientId,
        permissionType,
        error
      })
    })
  }

  const setRoxx = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord) => {
    return hasOldAndNewRequestable(rtconditions, 'roxx_api', 10, 1) &&
      vm.$globalVar.checkAutoRT(basicData, formatId, formatData, otherFormatData, 10, 'roxx_api')
  }

  const setRecruitMc = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord) => {
    return hasOldAndNewRequestable(rtconditions, 'recruit_mc_mail', 3, 1) &&
      vm.$globalVar.checkAutoRT(basicData, formatId, formatData, otherFormatData, 3, 'recruit_mc_mail')
  }

  const setNeocareer = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord) => {
    return hasOldAndNewRequestable(rtconditions, 'neocareer_admin', 4, 1) &&
      vm.$globalVar.checkAutoRT(basicData, formatId, formatData, otherFormatData, 4, 'neocareer_admin')
  }

  const setXMile = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord, permissionType: number, oldpermissionType: number) => {
    return hasOldAndNewRequestable(rtconditions, 'xmile_mail', permissionType, oldpermissionType) &&
      vm.$globalVar.checkAutoRT(basicData, formatId, formatData, otherFormatData, permissionType, 'xmile_mail')
  }

  const setPharmate = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord, permissionType: number, oldpermissionType: number) => {
    return hasOldAndNewRequestable(rtconditions, 'pharmate_mail', permissionType, oldpermissionType) &&
      vm.$globalVar.checkAutoRT(basicData, formatId, formatData, otherFormatData, permissionType, 'pharmate_mail')
  }

  const setAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord, permissionType: number, oldpermissionType: number, clientId: string) => {
    return hasOldAndNewRequestable(rtconditions, clientId, permissionType, oldpermissionType) &&
      vm.$globalVar.checkAutoRT(basicData, formatId, formatData, otherFormatData, permissionType, clientId)
  }

  const setAutoRTNewOnly = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord, permissionType: number, clientId: string) => {
    return hasRequestable(rtconditions, clientId, permissionType) &&
      vm.$globalVar.checkAutoRT(basicData, formatId, formatData, otherFormatData, permissionType, clientId)
  }

  const selectAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord): UnknownRecord | null => {
    let args: AutoRtArgs = [rtconditions, basicData, formatId, formatData, otherFormatData]

    if (vm.$globalVar.isShokumukeirekiFormat(formatId)) {
      args = [rtconditions, basicData, formatId, otherFormatData, formatData]
    }

    if (vm.$globalVar.isJobChangeFormat(formatId) && setPharmate(...args, 2, 1)) {
      return {
        logoImage: '/static/renew3_img/rt/pharmate_1_yagish_logo.png',
        clientId: 'pharmate_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社ファル・メイト',
        termurl: 'https://www.pharmate.jp/policy/',
        privacyurl: 'https://www.pharmate.jp/policy/',
        occupationurl: '',
        clientServiceName: 'Phar+Mate',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 2,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 5, 'eil100_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/eil100_5_yagish_logo.png',
        clientId: 'eil100_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社EIL',
        termurl: 'https://kaigoroute.com/terms/',
        privacyurl: 'https://kaigoroute.com/privacy/',
        occupationurl: '',
        clientServiceName: '株式会社EIL',
        permissionType: 5
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setRecruitMc(...args)) {
      return {
        logoImage: '/static/renew2_img/recruit_mc_1_yagish_log_20231023.png',
        clientId: 'recruit_mc_mail',
        dialogName: 'auto_rt',
        clientName: '(株)リクルートメディカルキャリア',
        termurl: 'https://cdn.p.recruit.co.jp/terms/ryz-t-1001/index.html',
        privacyurl: 'https://cdn.p.recruit.co.jp/terms/cmn-t-1002/index.html',
        occupationurl: '',
        clientServiceName: 'リクナビ薬剤師',
        privacyLabel: 'プライバシーポリシー',
        showTrailingBr: true,
        permissionType: 3,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 29, 28)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_28_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 29,
        oldPermissionType: 28
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 10, 1, 'jobs919_mail')) {
      return {
        logoImage: '/static/renew2_img/919kensetsu_yagish_logo_20230810.png',
        clientId: 'jobs919_mail',
        dialogName: 'auto_rt',
        clientName: '(株)クイック',
        termurl: 'https://plant.ten-navi.com/ruleprivacy/',
        privacyurl: 'https://plant.ten-navi.com/ruleprivacy/',
        occupationurl: '',
        clientServiceName: '建設・設備求人データベース',
        permissionType: 10,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 6, 2, 'recree_mail')) {
      return {
        logoImage: '/static/renew2_img/recree_yagish_logo_20240421.png',
        clientId: 'recree_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社レクリー',
        termurl: 'https://recree.jobree.co.jp/terms',
        privacyurl: 'https://recree.jobree.co.jp/privacy_policy',
        occupationurl: '',
        clientServiceName: 'ジョブリー建設',
        permissionType: 6,
        oldPermissionType: 2
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 30, 5)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_5_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 30,
        oldPermissionType: 5
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 31, 26)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_26_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 31,
        oldPermissionType: 26
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 32, 20)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_20_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 32,
        oldPermissionType: 20
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 33, 8)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_8_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 33,
        oldPermissionType: 8
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 34, 6)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_6_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 34,
        oldPermissionType: 6
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 35, 7)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_7_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 35,
        oldPermissionType: 7
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 36, 16)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_16_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 36,
        oldPermissionType: 16
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setXMile(...args, 37, 17)) {
      return {
        logoImage: '/static/renew3_img/rt/xmile_17_yagish_logo.png',
        clientId: 'xmile_mail',
        dialogName: 'auto_rt',
        clientName: 'X Mile株式会社',
        termurl: 'https://www.xmile.co.jp/policy/08',
        privacyurl: 'https://www.xmile.co.jp/policy/06',
        occupationurl: '',
        clientServiceName: 'クロスワーク',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 37,
        oldPermissionType: 17
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 11, 7, 'jobs919_mail')) {
      return {
        logoImage: '/static/renew2_img/919kensetsu_yagish_logo_20230810.png',
        clientId: 'jobs919_mail',
        dialogName: 'auto_rt',
        clientName: '(株)クイック',
        termurl: 'https://plant.ten-navi.com/ruleprivacy/',
        privacyurl: 'https://plant.ten-navi.com/ruleprivacy/',
        occupationurl: '',
        clientServiceName: '建設・設備求人データベース',
        permissionType: 11,
        oldPermissionType: 7
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 12, 2, 'jobs919_mail')) {
      return {
        logoImage: '/static/renew2_img/919jobs_yagish_logo_20230810.png',
        clientId: 'jobs919_mail',
        dialogName: 'auto_rt',
        clientName: '(株)クイック',
        termurl: 'https://plant.ten-navi.com/ruleprivacy/',
        privacyurl: 'https://plant.ten-navi.com/ruleprivacy/',
        occupationurl: '',
        clientServiceName: '整備士JOBS',
        permissionType: 12,
        oldPermissionType: 3
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 13, 8, 'jobs919_mail')) {
      return {
        logoImage: '/static/renew2_img/919jobs_yagish_logo_20230810.png',
        clientId: 'jobs919_mail',
        dialogName: 'auto_rt',
        clientName: '(株)クイック',
        termurl: 'https://plant.ten-navi.com/ruleprivacy/',
        privacyurl: 'https://plant.ten-navi.com/ruleprivacy/',
        occupationurl: '',
        clientServiceName: '整備士JOBS',
        permissionType: 13,
        oldPermissionType: 8
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 14, 9, 'jobs919_mail')) {
      return {
        logoImage: '/static/renew2_img/919jobs_yagish_logo_20230810.png',
        clientId: 'jobs919_mail',
        dialogName: 'auto_rt',
        clientName: '(株)クイック',
        termurl: 'https://plant.ten-navi.com/ruleprivacy/',
        privacyurl: 'https://plant.ten-navi.com/ruleprivacy/',
        occupationurl: '',
        clientServiceName: '整備士JOBS',
        permissionType: 14,
        oldPermissionType: 9
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 15, 4, 'jobs919_mail')) {
      return {
        logoImage: '/static/renew2_img/919kensetsu_yagish_logo_20230810.png',
        clientId: 'jobs919_mail',
        dialogName: 'auto_rt',
        clientName: '(株)クイック',
        termurl: 'https://plant.ten-navi.com/ruleprivacy/',
        privacyurl: 'https://plant.ten-navi.com/ruleprivacy/',
        occupationurl: '',
        clientServiceName: '建設・設備求人データベース',
        permissionType: 15,
        oldPermissionType: 4
      }
    }

    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 7, 2, 'plexjob_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/plexjob_1_yagish_logo.png',
        clientId: 'plexjob_mail',
        dialogName: 'plexjob-dialog',
        clientName: '株式会社プレックス',
        termurl: 'https://www.plex-job.com/terms/',
        privacyurl: 'https://www.plex-job.com/privacy-policy/',
        occupationurl: '',
        clientServiceName: 'PLEX JOB',
        permissionType: 7,
        oldPermissionType: 2
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 5, 4, 'tryt_api')) {
      return {
        logoImage: '/static/renew3_img/rt/tryt_4_yagish_logo.png',
        clientId: 'tryt_api',
        dialogName: 'tryt-dialog',
        clientName: '株式会社トライトキャリア',
        termurl: 'https://iryouworker.com/company/sitepolicy/',
        privacyurl: 'https://iryouworker.com/company/privacypolicy/',
        handlingurl: 'https://iryouworker.com/company/handling/',
        clientServiceName: '看護師ワーカー',
        occupationurl: '',
        permissionType: 5,
        oldPermissionType: 4
      }
    }
    if (vm.$globalVar.isNewFormat(formatId) && setNeocareer(...args)) {
      return {
        logoImage: '/static/renew2_img/neoe_yagish_logo_20230704.png',
        clientId: 'neocareer_admin',
        dialogName: 'auto_rt',
        clientName: '(株)ネオキャリア',
        termurl: 'https://www.s-agent.jp/agreement',
        privacyurl: 'https://www.neo-career.co.jp/policy/',
        occupationurl: '',
        clientServiceName: '就職Agent neo',
        privacyLabel: '個人情報の取り扱いについて',
        showTrailingBr: true,
        permissionType: 4,
        oldPermissionType: 1
      }
    }
    // per6はper4と同じ auto-rt（per5・per6 両方 request_able で per6 にアップグレード）
    if (vm.$globalVar.isNewFormat(formatId) && setAutoRT(...args, 6, 5, 'neocareer_admin')) {
      return {
        logoImage: '/static/renew2_img/neoe_yagish_logo_20230704.png',
        clientId: 'neocareer_admin',
        dialogName: 'auto_rt',
        clientName: '(株)ネオキャリア',
        termurl: 'なし',
        privacyurl: 'なし',
        occupationurl: '',
        clientServiceName: '就職Agent neo',
        permissionType: 6,
        oldPermissionType: 5
      }
    }
    if (vm.$globalVar.isNewFormat(formatId) && setAutoRTNewOnly(...args, 8, 'terracia_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/terracia_1_yagish_logo.png',
        clientId: 'terracia_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社テラシア',
        termurl: 'https://terracia-web.com/terms-of-service/',
        privacyurl: 'https://terracia-web.com/privacypolicy/',
        occupationurl: '',
        clientServiceName: '保育士テラス',
        permissionType: 8
      }
    }
    if (vm.$globalVar.isNewFormat(formatId) && setAutoRT(...args, 2, 1, 'hrteam_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/hrteam_1_yagish_logo.png',
        clientId: 'hrteam_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社HRteam',
        termurl: 'https://hr-team.co.jp/commit-terms/',
        privacyurl: 'https://hr-team.co.jp/privacy/',
        occupationurl: '',
        clientServiceName: 'ジョブコミット新卒',
        permissionType: 2,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isNewFormat(formatId) && setAutoRT(...args, 4, 3, 'hrteam_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/hrteam_4_yagish_logo.png',
        clientId: 'hrteam_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社HRteam',
        termurl: 'https://hr-team.co.jp/commit-terms/',
        privacyurl: 'https://hr-team.co.jp/privacy/',
        occupationurl: '',
        clientServiceName: 'ジョブコミット新卒',
        permissionType: 4,
        oldPermissionType: 3
      }
    }
    if (vm.$globalVar.isNewFormat(formatId) && setAutoRT(...args, 4, 3, 'careerdraft_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/careerdraft_3_yagish_logo.png',
        clientId: 'careerdraft_mail',
        dialogName: 'auto_rt',
        clientName: '絆ホールディングス(株)',
        termurl: 'https://d27rnpuamwvieu.cloudfront.net/pdf/0nvXg8Nheikk1vyMiwjjlMQXX.pdf',
        privacyurl: 'https://d27rnpuamwvieu.cloudfront.net/pdf/0J67ChgX0gawBQYVnQiXVSwXX.pdf',
        occupationurl: '',
        clientServiceName: 'キャリアドラフト',
        permissionType: 4,
        oldPermissionType: 3
      }
    }

    if (vm.$globalVar.isNewFormat(formatId) && setAutoRTNewOnly(...args, 2, 'cmindcareer_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/cmindcareer_1_yagish_logo.png',
        clientId: 'cmindcareer_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社シーマインドキャリア',
        termurl: 'https://cmindcareer.co.jp/privacy/',
        privacyurl: 'https://cmindcareer.co.jp/privacy/',
        occupationurl: '',
        clientServiceName: 'シーマインドキャリア',
        permissionType: 2
      }
    }

    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRT(...args, 23, 5, 'medley_link')) {
      return {
        logoImage: '/static/renew3_img/rt/job-medley-yagish-logo-20241119.png',
        clientId: 'medley_link',
        dialogName: 'auto_rt',
        clientName: '株式会社メドレー',
        termurl: 'https://job-medley.com/rule/',
        privacyurl: 'https://job-medley.com/rule/',
        occupationurl: '',
        clientServiceName: 'ジョブメドレー',
        permissionType: 23,
        oldPermissionType: 5
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 19, 'pleiades_mail')) {
      return {
        logoImage: '/static/renew2_img/pleiades_yagish_logo_2.png',
        clientId: 'pleiades_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社プレアデス',
        termurl: 'https://www.pleiades-net.co.jp/privacy/',
        privacyurl: 'https://www.pleiades-net.co.jp/privacy/',
        occupationurl: '',
        clientServiceName: '株式会社プレアデス',
        permissionType: 19,
        oldPermissionType: 2
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 20, 'pleiades_mail')) {
      return {
        logoImage: '/static/renew2_img/pleiades_yagish_logo_567.png',
        clientId: 'pleiades_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社プレアデス',
        termurl: 'https://www.pleiades-net.co.jp/privacy/',
        privacyurl: 'https://www.pleiades-net.co.jp/privacy/',
        occupationurl: '',
        clientServiceName: '株式会社プレアデス',
        permissionType: 20,
        oldPermissionType: 12
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 6, 1, 'nineearth_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/nineearth_1_yagish_logo.png',
        clientId: 'nineearth_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社N.E',
        termurl: 'https://nine-earth.co.jp/kaigo_s2/terms.html',
        privacyurl: 'https://nine-earth.co.jp/kaigo_s2/privacy.html',
        occupationurl: '',
        clientServiceName: '介護ナイン',
        permissionType: 6,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 7, 2, 'nineearth_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/nineearth_2_yagish_logo.png',
        clientId: 'nineearth_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社N.E',
        termurl: 'https://nine-earth.co.jp/kaigo_s2/terms.html',
        privacyurl: 'https://nine-earth.co.jp/kaigo_s2/privacy.html',
        occupationurl: '',
        clientServiceName: '介護ナイン',
        permissionType: 7,
        oldPermissionType: 2
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 4, 'eil100_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/eil100_4_yagish_logo.png',
        clientId: 'eil100_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社EIL',
        termurl: 'https://kaigoroute.com/terms/',
        privacyurl: 'https://kaigoroute.com/privacy/',
        occupationurl: '',
        clientServiceName: '株式会社EIL',
        permissionType: 4,
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 8, 5, 'nineearth_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/nineearth_5_yagish_logo.png',
        clientId: 'nineearth_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社N.E',
        termurl: 'https://nine-earth.co.jp/kaigo_s2/terms.html',
        privacyurl: 'https://nine-earth.co.jp/kaigo_s2/privacy.html',
        occupationurl: '',
        clientServiceName: '看護ナイン',
        permissionType: 8,
        oldPermissionType: 5
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 3, 'eil100_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/eil100_3_yagish_logo.png',
        clientId: 'eil100_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社EIL',
        termurl: 'https://kaigoroute.com/terms/',
        privacyurl: 'https://kaigoroute.com/privacy/',
        occupationurl: '',
        clientServiceName: '株式会社EIL',
        permissionType: 3
      }
    }
    if (vm.$globalVar.isJobChangeFormat(formatId) && setAutoRTNewOnly(...args, 6, 'peacekaigo_mail')) {
      return {
        logoImage: '/static/renew2_img/peacekaigo_yagish_logo_4.png',
        clientId: 'peacekaigo_mail',
        dialogName: 'auto_rt',
        clientName: 'ネクストイノベーション',
        termurl: 'https://peace-kango.com/terms/',
        privacyurl: 'https://peace-kango.com/company/privacy_policy/',
        occupationurl: '',
        clientServiceName: 'ネクストイノベーション',
        permissionType: 6,
        oldPermissionType: 5
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 2, 1, 'kikkakeagent_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/kikkakeagent_yagish_logo_20250326.png',
        clientId: 'kikkakeagent_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社キッカケクリエイション',
        termurl: 'https://kikkakeagent.co.jp/term-of-service',
        privacyurl: 'https://kikkakecreation.com/privacy/',
        occupationurl: '',
        clientServiceName: 'キッカケクリエイション_ITエンジニア',
        permissionType: 2,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setRoxx(...args)) {
      return {
        logoImage: '/static/renew2_img/zcareer-roxx-yagish-logo.png',
        clientId: 'roxx_api',
        dialogName: 'auto_rt',
        clientName: 'Zキャリア エージェント',
        termurl: 'https://agent.zcareer.com/terms/',
        privacyurl: 'https://roxx.co.jp/policy/privacy',
        occupationurl: '',
        clientServiceName: 'Zキャリア エージェント',
        privacyLabel: '個人情報保護方針',
        showTrailingBr: true,
        permissionType: 10,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isNewFormat(formatId) && setAutoRT(...args, 12, 11, 'roxx_api')) {
      return {
        logoImage: '/static/renew2_img/zcareer-roxx-yagish-logo.png',
        clientId: 'roxx_api',
        dialogName: 'auto_rt',
        clientName: '株式会社ROXX',
        termurl: 'https://agent.zcareer.com/terms/',
        privacyurl: 'https://roxx.co.jp/policy/privacy',
        occupationurl: '',
        clientServiceName: 'Zキャリア エージェント',
        permissionType: 12
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 17, 'roxx_api')) {
      return {
        logoImage: '/static/renew3_img/rt/roxx_13-16_yagish_logo.png',
        clientId: 'roxx_api',
        dialogName: 'auto_rt',
        clientName: '(株)ROXX',
        termurl: 'https://agent.zcareer.com/terms/',
        privacyurl: 'https://roxx.co.jp/policy/privacy',
        occupationurl: '',
        clientServiceName: 'Zキャリア',
        permissionType: 17
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 18, 'roxx_api')) {
      return {
        logoImage: '/static/renew3_img/rt/roxx_13-16_yagish_logo.png',
        clientId: 'roxx_api',
        dialogName: 'auto_rt',
        clientName: '(株)ROXX',
        termurl: 'https://agent.zcareer.com/terms/',
        privacyurl: 'https://roxx.co.jp/policy/privacy',
        occupationurl: '',
        clientServiceName: 'Zキャリア',
        permissionType: 18
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 19, 'roxx_api')) {
      return {
        logoImage: '/static/renew3_img/rt/roxx_13-16_yagish_logo.png',
        clientId: 'roxx_api',
        dialogName: 'auto_rt',
        clientName: '(株)ROXX',
        termurl: 'https://agent.zcareer.com/terms/',
        privacyurl: 'https://roxx.co.jp/policy/privacy',
        occupationurl: '',
        clientServiceName: 'Zキャリア',
        permissionType: 19
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 20, 'roxx_api')) {
      return {
        logoImage: '/static/renew3_img/rt/roxx_13-16_yagish_logo.png',
        clientId: 'roxx_api',
        dialogName: 'auto_rt',
        clientName: '(株)ROXX',
        termurl: 'https://agent.zcareer.com/terms/',
        privacyurl: 'https://roxx.co.jp/policy/privacy',
        occupationurl: '',
        clientServiceName: 'Zキャリア',
        permissionType: 20
      }
    }
    if (vm.$globalVar.isNewFormat(formatId) && setAutoRT(...args, 2, 1, 'onecareer_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/onecareer_1_yagish_logo.png',
        clientId: 'onecareer_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社ワンキャリア',
        termurl: 'https://www.onecareer.jp/infos/terms',
        privacyurl: 'https://www.onecareer.jp/infos/signup_privacy_policy',
        occupationurl: '',
        clientServiceName: 'ワンキャリア',
        permissionType: 2,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 7, 1, 'terracia_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/terracia_1_yagish_logo.png',
        clientId: 'terracia_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社テラシア',
        termurl: 'https://terracia-web.com/terms-of-service/',
        privacyurl: 'https://terracia-web.com/privacypolicy/',
        clientServiceName: '保育士テラス',
        permissionType: 7,
        oldPermissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 5, 3, 'terracia_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/terracia_1_yagish_logo.png',
        clientId: 'terracia_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社テラシア',
        termurl: 'https://terracia-web.com/terms-of-service/',
        privacyurl: 'https://terracia-web.com/privacypolicy/',
        occupationurl: '',
        clientServiceName: '保育士テラス',
        permissionType: 5,
        oldPermissionType: 3
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 6, 4, 'terracia_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/terracia_2_yagish_logo.png',
        clientId: 'terracia_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社テラシア',
        termurl: 'https://terracia-web.com/terms-of-service/',
        privacyurl: 'https://terracia-web.com/privacypolicy/',
        occupationurl: '',
        clientServiceName: '保育士テラス',
        permissionType: 6,
        oldPermissionType: 4
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 1, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_kaigo_1_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス_介護',
        termurl: 'https://www.kaigoagent.com/contents/tos.html',
        privacyurl: 'https://www.kaigoagent.com/contents/pmark.html',
        occupationurl: '',
        clientServiceName: 'カイゴジョブエージェント',
        permissionType: 1
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 2, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_kaigo_2_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス_介護',
        termurl: 'https://www.kaigoagent.com/contents/tos.html',
        privacyurl: 'https://www.kaigoagent.com/contents/pmark.html',
        occupationurl: '',
        clientServiceName: 'カイゴジョブエージェント',
        permissionType: 2
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 3, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_hoiku_3_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス_保育',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: '保育士人材バンク',
        permissionType: 3
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 4, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_hoiku_4_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス_保育',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: '保育士人材バンク',
        permissionType: 4
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 5, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_5_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: 'ナース専科',
        permissionType: 5
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 6, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_ptot_6_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス_PTOT',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: 'PTOT人材バンク',
        permissionType: 6
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 7, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_ptot_7_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス_PTOT',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: 'PTOT人材バンク',
        permissionType: 7
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 8, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_ptot_8_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス_PTOT',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: 'PTOT人材バンク',
        permissionType: 8
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 9, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_9_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: '検査技師バンク',
        permissionType: 9
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 10, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_10_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: '放射線技師バンク',
        permissionType: 10
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 11, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_11_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: '工学技師バンク',
        permissionType: 11
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRTNewOnly(...args, 12, 'sms_admin_new')) {
      return {
        logoImage: '/static/renew3_img/rt/sms_12_yagish_logo.png',
        clientId: 'sms_admin_new',
        dialogName: 'auto_rt',
        clientName: '株式会社エス･エム･エス',
        termurl: 'https://policy.bm-sms.co.jp/consumer/terms/jinzaibank-com',
        privacyurl: 'https://policy.bm-sms.co.jp/consumer/privacy/policy',
        occupationurl: '',
        clientServiceName: '国試黒本治療家エージェント',
        permissionType: 12
      }
    }
    if ((formatId === 'rerekisho_part_time' || formatId === 'rirekisho_part_time2') && setAutoRTNewOnly(...args, 9, 'terracia_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/terracia_1_yagish_logo.png',
        clientId: 'terracia_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社テラシア',
        termurl: 'https://terracia-web.com/terms-of-service/',
        privacyurl: 'https://terracia-web.com/privacypolicy/',
        occupationurl: '',
        clientServiceName: '保育士テラス',
        permissionType: 9
      }
    }
    if ((formatId === 'rerekisho_part_time' || formatId === 'rirekisho_part_time2') && setAutoRTNewOnly(...args, 10, 'terracia_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/terracia_1_yagish_logo.png',
        clientId: 'terracia_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社テラシア',
        termurl: 'https://terracia-web.com/terms-of-service/',
        privacyurl: 'https://terracia-web.com/privacypolicy/',
        occupationurl: '',
        clientServiceName: '保育士テラス',
        permissionType: 10
      }
    }
    if (vm.$globalVar.isJobChangeShokumukeirekiFormat(formatId) && setAutoRT(...args, 2, 1, 'primalwork_mail')) {
      return {
        logoImage: '/static/renew3_img/rt/primalwork_yagish_logo_20250326.jpg',
        clientId: 'primalwork_mail',
        dialogName: 'auto_rt',
        clientName: '株式会社プライマルワーク',
        termurl: 'https://primalwork.net/terms-of-service/',
        privacyurl: 'https://primalwork.net/privacy-policy/',
        occupationurl: '',
        clientServiceName: 'プライマルワーク',
        permissionType: 2,
        oldPermissionType: 1
      }
    }
    return null
  }

  const evaluateAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord) => {
    // 単一の真実源: 評価ごとに状態をリセットし、選定結果(どのRTを出すか)から表示・状態を一括適用する
    vm.$globalVar.autoRTInfo = createEmptyAutoRTInfo()
    vm.$globalVar.initAutoRT()
    vm.$globalVar.permissionType4RT = 0
    vm.$globalVar.oldPermissionType4RT = 0

    const selected = selectAutoRT(rtconditions, basicData, formatId, formatData, otherFormatData)
    if (!selected) {
      return null
    }

    vm.$globalVar.autoRTInfo = selected
    vm.$globalVar.isAutoRTable = true
    vm.$globalVar.isAutoRT4Rtable = true
    vm.$globalVar.permissionType4RT = Number(selected.permissionType) || 0
    vm.$globalVar.oldPermissionType4RT = Number(selected.oldPermissionType) || 0
    displayDialog(formatId, String(selected.clientId), Number(selected.permissionType))
    return selected
  }

  return {
    evaluateAutoRT,
    selectAutoRT,
    setRoxx,
    setRecruitMc,
    setNeocareer,
    setXMile,
    setPharmate,
    setAutoRT,
    setAutoRTNewOnly
  }
}
